import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, InconsistencyTypes } from '@/app/utils/constants';
import { SearchFocusIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { useGetSegmentInconsistencies } from '@/app/components/features/evaluations/Conflicts/hooks/useSegmentInconsistencies';
import { ConflictsDetailPageContent } from '@/app/components/features/evaluations/Conflicts/ConflictsDetailPageContent';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { flattenConflicts } from '@/app/components/features/evaluations/Conflicts/flattenConflicts';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/conflicts/$conflictId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.conflicts');
  const { tenderPackageId, projectId, conflictId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const { data: analysisData } = useGetSegmentInconsistencies(
    tenderPackageId,
    true,
    InconsistencyTypes.TENDER_PACKAGE,
  );

  const flatRows = useMemo(() => flattenConflicts(analysisData), [analysisData]);
  const item = flatRows.find((r) => r.index === parseInt(conflictId, 10));

  const rowsWithId = useMemo(
    () => flatRows.map((row) => ({ ...row, id: String(row.index) })),
    [flatRows],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(rowsWithId, conflictId, (id) => ({
      to: '/project/$projectId/tender-packages/$tenderPackageId/conflicts/$conflictId',
      params: { projectId, tenderPackageId, conflictId: id },
    }));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/conflicts"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/conflicts"
                params={{ projectId, tenderPackageId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{item?.finding.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <ConflictsDetailPageContent
        resourceId={tenderPackageId}
        inconsistencyType={InconsistencyTypes.TENDER_PACKAGE}
        conflictId={conflictId}
      />
    </>
  );
}
